import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import MessageVariablesPicker from "../MessageVariablesPicker";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("whatsappModal.formErrors.name.short"))
    .max(50, i18n.t("whatsappModal.formErrors.name.long"))
    .required(i18n.t("whatsappModal.formErrors.name.required")),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const initialState = {
    name: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    token: "",
    provider: "beta",
    //timeSendQueue: 0,
    //sendIdQueue: 0,
    expiresInactiveMessage: "",
    expiresTicket: 0,
    timeUseBotQueues: 0,
    maxUseBotQueues: 3,
    integration: null,
  };

  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queues, setQueues] = useState([]);
  const [selectedQueueId, setSelectedQueueId] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  

  const fieldRefs = useRef({});
  const caretRef = useRef({});

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);

        setWhatsApp(data);
        setSelectedPrompt(data.promptId);
        setSelectedIntegration(data.integrationId);
        

        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
        setSelectedQueueId(data.transferQueueId);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);

        const { data: dataIntegration } = await api.get("/queueIntegration");
        setIntegrations(dataIntegration.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const handleSaveWhatsApp = async (values) => {
    const whatsappData = {
      ...values,
      queueIds: selectedQueueIds,
      transferQueueId: selectedQueueId,
      promptId: selectedPrompt ? selectedPrompt : null,
      integrationId: selectedIntegration,
       provider: "beta",
    };
    delete whatsappData["queues"];
    delete whatsappData["session"];

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleChangeQueue = (e) => {
    setSelectedQueueIds(e);
    setSelectedPrompt(null);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
    setSelectedQueueIds([]);
  };

  const handleChangeIntegration = (e) => {
    setSelectedIntegration(e.target.value);
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setSelectedQueueId(null);
    setSelectedQueueIds([]);
   
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) =>
            // helper: adiciona variável no final do campo indicado
            // mantém comportamento atual e evita repetir código

            (() => {
              const rememberCaret = (field) => (e) => {
                const el = e?.target;
                if (
                  el &&
                  typeof el.selectionStart === "number" &&
                  typeof el.selectionEnd === "number"
                ) {
                  caretRef.current[field] = {
                    start: el.selectionStart,
                    end: el.selectionEnd,
                  };
                }
              };

              const insertVar = (field) => (value) => {
                const current = String(values[field] ?? "");
                const caret = caretRef.current[field];

                // Sem cursor (nunca focou / sem seleção registrada) => adiciona no fim
                if (
                  !caret ||
                  typeof caret.start !== "number" ||
                  typeof caret.end !== "number"
                ) {
                  setFieldValue(field, `${current}${value}`);
                  return;
                }

                let start = Math.max(0, Math.min(caret.start, current.length));
                let end = Math.max(0, Math.min(caret.end, current.length));
                if (end < start) [start, end] = [end, start];

                const next =
                  current.slice(0, start) + value + current.slice(end);
                setFieldValue(field, next);

                // restaura cursor após render
                setTimeout(() => {
                  const el = fieldRefs.current[field];
                  if (!el || typeof el.setSelectionRange !== "function") return;
                  try {
                    el.focus();
                    const pos = start + String(value).length;
                    el.setSelectionRange(pos, pos);
                    caretRef.current[field] = { start: pos, end: pos };
                  } catch (e) {}
                }, 0);
              };

              return (
                <Form>
                  <DialogContent dividers>
                    <div className={classes.multFieldLine}>
                      <Grid spacing={2} container>
                        <Grid item>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid style={{ paddingTop: 15 }} item>
                          <FormControlLabel
                            control={
                              <Field
                                as={Switch}
                                color="primary"
                                name="isDefault"
                                checked={values.isDefault}
                              />
                            }
                            label={i18n.t("whatsappModal.form.default")}
                          />
                        </Grid>
                      </Grid>
                    </div>
                    {/* Mensagens + Variáveis (75% / 25%) */}
                    <Grid container spacing={1} alignItems="flex-start">
                      <Grid
                        item
                        xs={12}
                        md={10}
                        style={{ flexBasis: "75%", maxWidth: "75%" }}
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.greetingMessage")}
                          type="greetingMessage"
                          multiline
                          rows={4}
                          fullWidth
                          name="greetingMessage"
                          inputRef={(el) =>
                            (fieldRefs.current.greetingMessage = el)
                          }
                          onSelect={rememberCaret("greetingMessage")}
                          onKeyUp={rememberCaret("greetingMessage")}
                          onClick={rememberCaret("greetingMessage")}
                          onMouseUp={rememberCaret("greetingMessage")}
                          error={
                            touched.greetingMessage &&
                            Boolean(errors.greetingMessage)
                          }
                          helperText={
                            touched.greetingMessage && errors.greetingMessage
                          }
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={2}
                        style={{ flexBasis: "25%", maxWidth: "25%" }}
                      >
                        <MessageVariablesPicker
                          onClick={insertVar("greetingMessage")}
                          disabled={isSubmitting}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={1} alignItems="flex-start">
                      <Grid
                        item
                        xs={12}
                        md={10}
                        style={{ flexBasis: "75%", maxWidth: "75%" }}
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.complationMessage")}
                          type="complationMessage"
                          multiline
                          rows={4}
                          fullWidth
                          name="complationMessage"
                          inputRef={(el) =>
                            (fieldRefs.current.complationMessage = el)
                          }
                          onSelect={rememberCaret("complationMessage")}
                          onKeyUp={rememberCaret("complationMessage")}
                          onClick={rememberCaret("complationMessage")}
                          onMouseUp={rememberCaret("complationMessage")}
                          error={
                            touched.complationMessage &&
                            Boolean(errors.complationMessage)
                          }
                          helperText={
                            touched.complationMessage &&
                            errors.complationMessage
                          }
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={2}
                        style={{ flexBasis: "25%", maxWidth: "25%" }}
                      >
                        <MessageVariablesPicker
                          onClick={insertVar("complationMessage")}
                          disabled={isSubmitting}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={1} alignItems="flex-start">
                      <Grid
                        item
                        xs={12}
                        md={10}
                        style={{ flexBasis: "75%", maxWidth: "75%" }}
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.outOfHoursMessage")}
                          type="outOfHoursMessage"
                          multiline
                          rows={4}
                          fullWidth
                          name="outOfHoursMessage"
                          inputRef={(el) =>
                            (fieldRefs.current.outOfHoursMessage = el)
                          }
                          onSelect={rememberCaret("outOfHoursMessage")}
                          onKeyUp={rememberCaret("outOfHoursMessage")}
                          onClick={rememberCaret("outOfHoursMessage")}
                          onMouseUp={rememberCaret("outOfHoursMessage")}
                          error={
                            touched.outOfHoursMessage &&
                            Boolean(errors.outOfHoursMessage)
                          }
                          helperText={
                            touched.outOfHoursMessage &&
                            errors.outOfHoursMessage
                          }
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={2}
                        style={{ flexBasis: "25%", maxWidth: "25%" }}
                      >
                        <MessageVariablesPicker
                          onClick={insertVar("outOfHoursMessage")}
                          disabled={isSubmitting}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={1} alignItems="flex-start">
                      <Grid
                        item
                        xs={12}
                        md={10}
                        style={{ flexBasis: "75%", maxWidth: "75%" }}
                      >
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.ratingMessage")}
                          type="ratingMessage"
                          multiline
                          rows={4}
                          fullWidth
                          name="ratingMessage"
                          inputRef={(el) =>
                            (fieldRefs.current.ratingMessage = el)
                          }
                          onSelect={rememberCaret("ratingMessage")}
                          onKeyUp={rememberCaret("ratingMessage")}
                          onClick={rememberCaret("ratingMessage")}
                          onMouseUp={rememberCaret("ratingMessage")}
                          error={
                            touched.ratingMessage &&
                            Boolean(errors.ratingMessage)
                          }
                          helperText={
                            touched.ratingMessage && errors.ratingMessage
                          }
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={2}
                        style={{ flexBasis: "25%", maxWidth: "25%" }}
                      >
                        <MessageVariablesPicker
                          onClick={insertVar("ratingMessage")}
                          disabled={isSubmitting}
                        />
                      </Grid>
                    </Grid>

                    <div>
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.token")}
                        type="token"
                        fullWidth
                        name="token"
                        variant="outlined"
                        margin="dense"
                      />
                    </div>
                    <QueueSelect
                      selectedQueueIds={selectedQueueIds}
                      onChange={(selectedIds) => handleChangeQueue(selectedIds)}
                    />
                    <FormControl margin="dense" variant="outlined" fullWidth>
                      <InputLabel>
                        {i18n.t("whatsappModal.form.prompt")}
                      </InputLabel>
                      <Select
                        labelId="dialog-select-prompt-label"
                        id="dialog-select-prompt"
                        name="promptId"
                        value={selectedPrompt || ""}
                        onChange={handleChangePrompt}
                        label={i18n.t("whatsappModal.form.prompt")}
                        fullWidth
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          getContentAnchorEl: null,
                        }}
                      >
                        {prompts.map((prompt) => (
                          <MenuItem key={prompt.id} value={prompt.id}>
                            {prompt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl margin="dense" variant="outlined" fullWidth>
                      <InputLabel>
                        {i18n.t("whatsappModal.form.integration")}
                      </InputLabel>
                      <Select
                        labelId="dialog-select-integration-label"
                        id="dialog-select-integration"
                        name="promptId"
                        value={selectedIntegration || ""}
                        onChange={handleChangeIntegration}
                        label={i18n.t("whatsappModal.form.integration")}
                        fullWidth
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          getContentAnchorEl: null,
                        }}
                      >
                        {integrations.map((prompt) => (
                          <MenuItem key={prompt.id} value={prompt.id}>
                            {prompt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <div>
                      <h3>{i18n.t("whatsappModal.form.queueRedirection")}</h3>
                      <p>{i18n.t("whatsappModal.form.queueRedirectionDesc")}</p>
                      <Grid container spacing={2}>
                        <Grid item sm={6}>
                          <Field
                            fullWidth
                            type="number"
                            as={TextField}
                            label={i18n.t("whatsappModal.form.timeToTransfer")}
                            name="timeToTransfer"
                            error={
                              touched.timeToTransfer &&
                              Boolean(errors.timeToTransfer)
                            }
                            helperText={
                              touched.timeToTransfer && errors.timeToTransfer
                            }
                            variant="outlined"
                            margin="dense"
                            className={classes.textField}
                            InputLabelProps={{
                              shrink: values.timeToTransfer ? true : false,
                            }}
                          />
                        </Grid>

                        <Grid item sm={6}>
                          <QueueSelect
                            selectedQueueIds={selectedQueueId}
                            onChange={(selectedId) => {
                              setSelectedQueueId(selectedId);
                            }}
                            multiple={false}
                            title={i18n.t("whatsappModal.form.queue")}
                          />
                        </Grid>
                      </Grid>
                      <Grid spacing={2} container>
                        {/* ENCERRAR CHATS ABERTOS APÓS X HORAS */}
                        <Grid xs={12} md={12} item>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.expiresTicket")}
                            fullWidth
                            name="expiresTicket"
                            variant="outlined"
                            margin="dense"
                            error={
                              touched.expiresTicket &&
                              Boolean(errors.expiresTicket)
                            }
                            helperText={
                              touched.expiresTicket && errors.expiresTicket
                            }
                          />
                        </Grid>
                      </Grid>
                      {/* MENSAGEM POR INATIVIDADE*/}
                      <Grid container spacing={1} alignItems="flex-start">
                        <Grid
                          item
                          xs={12}
                          md={10}
                          style={{ flexBasis: "75%", maxWidth: "75%" }}
                        >
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "whatsappModal.form.expiresInactiveMessage"
                            )}
                            multiline
                            rows={4}
                            fullWidth
                            name="expiresInactiveMessage"
                            inputRef={(el) =>
                              (fieldRefs.current.expiresInactiveMessage = el)
                            }
                            onSelect={rememberCaret("expiresInactiveMessage")}
                            onKeyUp={rememberCaret("expiresInactiveMessage")}
                            onClick={rememberCaret("expiresInactiveMessage")}
                            onMouseUp={rememberCaret("expiresInactiveMessage")}
                            error={
                              touched.expiresInactiveMessage &&
                              Boolean(errors.expiresInactiveMessage)
                            }
                            helperText={
                              touched.expiresInactiveMessage &&
                              errors.expiresInactiveMessage
                            }
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={2}
                          style={{ flexBasis: "25%", maxWidth: "25%" }}
                        >
                          <MessageVariablesPicker
                            onClick={insertVar("expiresInactiveMessage")}
                            disabled={isSubmitting}
                          />
                        </Grid>
                      </Grid>
                    </div>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleClose}
                      color="secondary"
                      disabled={isSubmitting}
                      variant="outlined"
                    >
                      {i18n.t("whatsappModal.buttons.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                      variant="contained"
                      className={classes.btnWrapper}
                    >
                      {whatsAppId
                        ? i18n.t("whatsappModal.buttons.okEdit")
                        : i18n.t("whatsappModal.buttons.okAdd")}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </Button>
                  </DialogActions>
                </Form>
              );
            })()
          }
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
